/* =====================================================================
   ColdCake 3D Cake Widget — single-layer cake with shape + mode support
   - data-shape="rect"     → rectangular slab (hero default)
   - data-shape="cylinder" → classic round birthday cake (PDP)
   - data-mode="scroll"    → scroll-tilt animation (hero)
   - data-mode="pdp"       → static tilt via data-tilt-static
   Live API:
     window.coldcakeCakeRebuild(widget)        — full rebuild on shape/size change
     window.coldcakeCakeApplyImage(widget,src) — re-paint top texture
   ===================================================================== */
(function () {
  'use strict';

  function ensureCanvas(widget) {
    var c = widget.querySelector('canvas');
    if (!c) {
      c = document.createElement('canvas');
      c.style.cssText = 'width:100%; height:100%; display:block;';
      widget.appendChild(c);
    }
    return c;
  }

  function init(widget) {
    var canvas = ensureCanvas(widget);
    if (typeof THREE === 'undefined') {
      var fb = widget.querySelector('.cc-cake-3d__fallback');
      if (fb) fb.style.opacity = '1';
      return;
    }

    var COLOR_CAKE = widget.dataset.cakeColor || '#FFFFFF';
    var COLOR_ICING = widget.dataset.icingColor || '#FFFFFF';
    var COLOR_ROSETTE = widget.dataset.rosetteColor || '#E84A28';
    var COLOR_BOARD = widget.dataset.boardColor || '#E84A28';
    var TILT_START = parseFloat(widget.dataset.tiltStart || '70');
    var TILT_END = parseFloat(widget.dataset.tiltEnd || '7');
    var SHAPE = widget.dataset.shape || 'rect';
    var MODE = widget.dataset.mode || 'scroll';
    var STATIC_TILT = parseFloat(widget.dataset.tiltStatic || '22');
    var initialImage = widget.dataset.cakeImage || '';

    var topMaterial = null;
    var defaultTexture = null;
    var rafId = null;
    var rendererRef = null;

    function buildDefaultTopTexture() {
      var c = document.createElement('canvas');
      c.width = 1024; c.height = 768;
      var ctx = c.getContext('2d');
      var grad = ctx.createLinearGradient(0, 0, 0, 768);
      grad.addColorStop(0, '#FFFEFB'); grad.addColorStop(0.5, '#FFF8EC'); grad.addColorStop(1, '#FFECD1');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, 1024, 768);
      ctx.globalAlpha = 0.14; ctx.strokeStyle = '#F4DEB8'; ctx.lineWidth = 24;
      for (var i = 0; i < 6; i++) {
        ctx.beginPath();
        ctx.moveTo(-50 + i * 60, 100 + i * 80);
        ctx.bezierCurveTo(300, 50 + i * 30, 700, 600 - i * 40, 1100, 400 + i * 50);
        ctx.stroke();
      }
      ctx.globalAlpha = 0.08;
      for (var n = 0; n < 380; n++) {
        ctx.fillStyle = Math.random() > 0.5 ? '#F4DEB8' : '#E0C99A';
        ctx.fillRect(Math.random() * 1024, Math.random() * 768, 2, 2);
      }
      ctx.globalAlpha = 1;
      var tex = new THREE.CanvasTexture(c);
      if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 8;
      return tex;
    }

    function buildSideMaps() {
      var W = 1024, H = 256;
      var dc = document.createElement('canvas');
      dc.width = W; dc.height = H;
      var dx = dc.getContext('2d');
      dx.fillStyle = COLOR_CAKE; dx.fillRect(0, 0, W, H);
      var lineCount = 36;
      for (var i = 0; i < lineCount; i++) {
        var y = (i + 0.5) * (H / lineCount);
        dx.fillStyle = 'rgba(220,200,170,0.12)'; dx.fillRect(0, y, W, 1);
        dx.fillStyle = 'rgba(255,255,255,0.18)'; dx.fillRect(0, y - 1.5, W, 1);
      }
      dx.globalAlpha = 0.05;
      for (var n = 0; n < 800; n++) {
        dx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#D8C2A0';
        dx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
      }
      dx.globalAlpha = 1;
      var diffuse = new THREE.CanvasTexture(dc);
      if (THREE.SRGBColorSpace) diffuse.colorSpace = THREE.SRGBColorSpace;

      var nc = document.createElement('canvas');
      nc.width = W; nc.height = H;
      var nx = nc.getContext('2d');
      nx.fillStyle = 'rgb(128,128,255)'; nx.fillRect(0, 0, W, H);
      for (var j = 0; j < lineCount; j++) {
        var yy = (j + 0.5) * (H / lineCount);
        var ng1 = nx.createLinearGradient(0, yy - 3, 0, yy);
        ng1.addColorStop(0, 'rgb(128,128,255)'); ng1.addColorStop(1, 'rgb(128,168,232)');
        nx.fillStyle = ng1; nx.fillRect(0, yy - 3, W, 3);
        var ng2 = nx.createLinearGradient(0, yy, 0, yy + 3);
        ng2.addColorStop(0, 'rgb(128,88,232)'); ng2.addColorStop(1, 'rgb(128,128,255)');
        nx.fillStyle = ng2; nx.fillRect(0, yy, W, 3);
      }
      var normal = new THREE.CanvasTexture(nc);
      return { diffuse: diffuse, normal: normal };
    }

    function buildRosette(color, size) {
      var radius = size || 0.14;
      var ridges = 6, amp = 0.22, twist = 0.7;
      var geo = new THREE.SphereGeometry(radius, 28, 18);
      var pos = geo.attributes.position;
      var v = new THREE.Vector3();
      for (var i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        var rho = Math.sqrt(v.x * v.x + v.z * v.z);
        if (rho > 1e-5) {
          var ang = Math.atan2(v.z, v.x);
          var phase = ang + (v.y / radius) * twist;
          var ridge = Math.cos(phase * ridges) * 0.5 + 0.5;
          var k = 1 + (ridge - 0.5) * amp * 2;
          v.x *= k; v.z *= k;
        }
        var yNorm = v.y / radius;
        v.y = v.y * 1.05 + Math.max(0, yNorm) * radius * 0.05;
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      pos.needsUpdate = true;
      geo.computeVertexNormals();
      var mat = new THREE.MeshStandardMaterial({
        color: color, roughness: 0.18, metalness: 0.0,
        emissive: color, emissiveIntensity: 0.32
      });
      var mesh = new THREE.Mesh(geo, mat);
      mesh.scale.set(1.0, 0.85, 1.0);
      return mesh;
    }

    function buildRectBorder(lengthX, lengthZ, y, color, size) {
      var group = new THREE.Group();
      var step = (size || 0.14) * 1.55;
      var halfX = lengthX / 2, halfZ = lengthZ / 2;
      var nX = Math.max(2, Math.round(lengthX / step));
      var sX = lengthX / nX;
      for (var i = 0; i <= nX; i++) {
        var x = -halfX + i * sX;
        var f = buildRosette(color, size); f.position.set(x, y, halfZ); f.rotation.y = Math.random() * Math.PI * 2; group.add(f);
        var b = buildRosette(color, size); b.position.set(x, y, -halfZ); b.rotation.y = Math.random() * Math.PI * 2; group.add(b);
      }
      var nZ = Math.max(2, Math.round(lengthZ / step));
      var sZ = lengthZ / nZ;
      for (var j = 1; j < nZ; j++) {
        var z = -halfZ + j * sZ;
        var r = buildRosette(color, size); r.position.set(halfX, y, z); r.rotation.y = Math.random() * Math.PI * 2; group.add(r);
        var l = buildRosette(color, size); l.position.set(-halfX, y, z); l.rotation.y = Math.random() * Math.PI * 2; group.add(l);
      }
      return group;
    }

    function buildCircularBorder(radius, y, color, size) {
      var group = new THREE.Group();
      var step = (size || 0.14) * 1.55;
      var circ = 2 * Math.PI * radius;
      var n = Math.max(16, Math.round(circ / step));
      for (var i = 0; i < n; i++) {
        var ang = (i / n) * Math.PI * 2;
        var r = buildRosette(color, size);
        r.position.set(Math.cos(ang) * radius, y, Math.sin(ang) * radius);
        r.rotation.y = -ang;
        group.add(r);
      }
      return group;
    }

    function buildBoardRect(w, d, thickness, color) {
      var shape = new THREE.Shape();
      var r = 0.18, hw = w / 2, hd = d / 2;
      shape.moveTo(-hw + r, -hd);
      shape.lineTo(hw - r, -hd); shape.quadraticCurveTo(hw, -hd, hw, -hd + r);
      shape.lineTo(hw, hd - r); shape.quadraticCurveTo(hw, hd, hw - r, hd);
      shape.lineTo(-hw + r, hd); shape.quadraticCurveTo(-hw, hd, -hw, hd - r);
      shape.lineTo(-hw, -hd + r); shape.quadraticCurveTo(-hw, -hd, -hw + r, -hd);
      var geom = new THREE.ExtrudeGeometry(shape, {
        depth: thickness, bevelEnabled: true,
        bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 4, curveSegments: 10
      });
      geom.rotateX(-Math.PI / 2);
      geom.translate(0, -thickness, 0);
      return new THREE.Mesh(geom, new THREE.MeshStandardMaterial({ color: color, roughness: 0.28, metalness: 0.0, emissive: color, emissiveIntensity: 0.18 }));
    }

    function buildBoardCircle(radius, thickness, color) {
      var geom = new THREE.CylinderGeometry(radius, radius, thickness, 64);
      var mat = new THREE.MeshStandardMaterial({ color: color, roughness: 0.28, metalness: 0.0, emissive: color, emissiveIntensity: 0.18 });
      var mesh = new THREE.Mesh(geom, mat);
      mesh.position.y = -thickness / 2;
      return mesh;
    }

    function build() {
      var w = canvas.clientWidth, h = canvas.clientHeight;
      var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
      rendererRef = renderer;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h, false);
      if (THREE.sRGBEncoding) renderer.outputEncoding = THREE.sRGBEncoding;

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(30, w / h, 0.1, 100);
      camera.position.set(0, 4.0, 11.5);
      camera.lookAt(0, -0.2, 0);

      scene.add(new THREE.AmbientLight(0xfff0d8, 0.55));
      var key = new THREE.DirectionalLight(0xfff5e8, 0.62); key.position.set(4, 6, 4); scene.add(key);
      var fill = new THREE.DirectionalLight(0xffe8c8, 0.38); fill.position.set(-4, 3, 3); scene.add(fill);
      var rim = new THREE.DirectionalLight(parseInt(COLOR_ROSETTE.slice(1), 16), 0.42); rim.position.set(-3, 1.6, -3); scene.add(rim);
      var topL = new THREE.DirectionalLight(0xfff8ea, 0.20); topL.position.set(0, 9, 0); scene.add(topL);
      scene.add(new THREE.HemisphereLight(0xfff8ec, 0xf4deb8, 0.32));

      defaultTexture = buildDefaultTopTexture();
      var sideMaps = buildSideMaps();
      var sideMat = new THREE.MeshStandardMaterial({
        map: sideMaps.diffuse, normalMap: sideMaps.normal,
        normalScale: new THREE.Vector2(0.85, 0.85),
        color: 0xFFFFFF, roughness: 0.78, metalness: 0.0
      });
      var bottomMat = new THREE.MeshStandardMaterial({ color: 0xFFFAF0, roughness: 0.9 });
      topMaterial = new THREE.MeshBasicMaterial({ map: defaultTexture, color: 0xffffff });

      var cake = new THREE.Group();
      var board, slabH;

      if (SHAPE === 'cylinder') {
        var cylR = 1.6, cylH = 0.95, cylSeg = 64;
        slabH = cylH;
        var cylGeo = new THREE.CylinderGeometry(cylR, cylR, cylH, cylSeg);
        var cyl = new THREE.Mesh(cylGeo, [sideMat, topMaterial, bottomMat]);
        cake.add(cyl);
        cake.add(buildCircularBorder(cylR + 0.02, cylH / 2 + 0.04, COLOR_ROSETTE, 0.13));
        cake.add(buildCircularBorder(cylR + 0.02, -cylH / 2 + 0.06, COLOR_ROSETTE, 0.16));
        board = buildBoardCircle(cylR + 0.55, 0.12, COLOR_BOARD);
        board.position.y = -cylH / 2 - 0.05;
      } else {
        var slabW = 4.0, slabD = 2.85;
        slabH = 0.78;
        var slab = new THREE.Mesh(
          new THREE.BoxGeometry(slabW, slabH, slabD, 1, 1, 1),
          [sideMat, sideMat, topMaterial, bottomMat, sideMat, sideMat]
        );
        cake.add(slab);
        cake.add(buildRectBorder(slabW - 0.04, slabD - 0.04, slabH / 2 + 0.06, COLOR_ROSETTE, 0.13));
        cake.add(buildRectBorder(slabW - 0.04, slabD - 0.04, -slabH / 2 + 0.07, COLOR_ROSETTE, 0.16));
        board = buildBoardRect(slabW + 0.85, slabD + 0.85, 0.12, COLOR_BOARD);
        board.position.y = -slabH / 2 - 0.05;
      }

      var shadow = new THREE.Mesh(
        new THREE.CircleGeometry(3.2, 48),
        new THREE.MeshBasicMaterial({ color: 0x1a1a1a, transparent: true, opacity: 0.22 })
      );
      shadow.rotation.x = -Math.PI / 2;
      shadow.position.y = -slabH / 2 - 0.18;
      shadow.scale.set(1.3, 0.85, 1);

      var tiltGroup = new THREE.Group();
      tiltGroup.add(shadow); tiltGroup.add(board); tiltGroup.add(cake);
      scene.add(tiltGroup);

      function deg(d) { return d * Math.PI / 180; }
      function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
      tiltGroup.rotation.x = deg(MODE === 'pdp' ? STATIC_TILT : TILT_START) * 0.70;
      tiltGroup.rotation.y = -0.18;

      var fb = widget.querySelector('.cc-cake-3d__fallback');
      if (fb) fb.style.opacity = '0';

      if (initialImage) applyImage(initialImage);

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function tick() {
        if (MODE === 'pdp') {
          var targetX = deg(STATIC_TILT) * 0.70;
          tiltGroup.rotation.x += (targetX - tiltGroup.rotation.x) * 0.08;
        } else {
          var rect = canvas.getBoundingClientRect();
          var vh = window.innerHeight;
          var progress = clamp(1 - (rect.top + rect.height * 0.5) / vh, 0, 1);
          var currentTilt = TILT_START + (TILT_END - TILT_START) * progress;
          var targetX2 = deg(currentTilt) * 0.70;
          tiltGroup.rotation.x += (targetX2 - tiltGroup.rotation.x) * 0.08;
        }

        if (reducedMotion) {
          tiltGroup.rotation.y = -0.18;
          tiltGroup.scale.set(1, 1, 1);
        } else {
          var t = performance.now();
          tiltGroup.rotation.y = -0.18 + Math.sin(t / 4200) * 0.07;
          var pulse = 1 + Math.sin(t / 2800) * 0.012;
          tiltGroup.scale.set(pulse, pulse, pulse);
        }

        renderer.render(scene, camera);
        rafId = requestAnimationFrame(tick);
      }
      rafId = requestAnimationFrame(tick);

      var rt;
      var resizeHandler = function () {
        clearTimeout(rt);
        rt = setTimeout(function () {
          var w2 = canvas.clientWidth, h2 = canvas.clientHeight;
          camera.aspect = w2 / h2;
          camera.updateProjectionMatrix();
          renderer.setSize(w2, h2, false);
        }, 100);
      };
      window.addEventListener('resize', resizeHandler);

      widget._cc = {
        applyImage: applyImage,
        destroy: function () {
          if (rafId) cancelAnimationFrame(rafId);
          window.removeEventListener('resize', resizeHandler);
          if (rendererRef) {
            rendererRef.dispose();
            if (rendererRef.forceContextLoss) rendererRef.forceContextLoss();
            rendererRef = null;
          }
          var c2 = widget.querySelector('canvas');
          if (c2 && c2.parentNode) c2.parentNode.removeChild(c2);
          delete widget._cc;
        }
      };
    }

    function applyImage(src) {
      if (!topMaterial) return;
      if (!src) {
        topMaterial.map = defaultTexture;
        topMaterial.needsUpdate = true;
        return;
      }
      var loader = new THREE.TextureLoader();
      loader.crossOrigin = 'anonymous';
      loader.load(src, function (tex) {
        var img = tex.image;
        var isCylinder = SHAPE === 'cylinder';
        var cakeAR = isCylinder ? 1.0 : (4.0 / 2.85);
        var w = 2048, h = Math.round(w / cakeAR);
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        var ctx = c.getContext('2d');
        var ar = img.width / img.height;
        var dw, dh, dx, dy;

        if (isCylinder) {
          // Cylinder mode — inscribe full logo INSIDE the visible circle so nothing gets clipped.
          // Math: max bounding-box inside circle of radius r given aspect ratio ar:
          //   maxH = 2r / sqrt(ar²+1), maxW = maxH * ar
          ctx.fillStyle = COLOR_ICING; ctx.fillRect(0, 0, w, h);
          var rad = w / 2;
          var maxH = (2 * rad) / Math.sqrt(ar * ar + 1);
          var maxW = maxH * ar;
          dw = maxW * 0.96; // 96% margin so logo doesn't kiss the circle edge
          dh = maxH * 0.96;
          dx = (w - dw) / 2;
          dy = (h - dh) / 2;
        } else {
          // Rect mode — cover-fit on desktop, contain-fit on mobile (existing behaviour)
          var isDesktop = window.matchMedia('(min-width: 880px)').matches;
          if (isDesktop) {
            if (ar > cakeAR) { dh = h; dw = h * ar; dx = (w - dw) / 2; dy = 0; }
            else { dw = w; dh = w / ar; dx = 0; dy = (h - dh) / 2; }
          } else {
            ctx.fillStyle = COLOR_ICING; ctx.fillRect(0, 0, w, h);
            if (ar > cakeAR) { dw = w; dh = w / ar; dx = 0; dy = (h - dh) / 2; }
            else { dh = h; dw = h * ar; dx = (w - dw) / 2; dy = 0; }
          }
        }

        ctx.save();
        ctx.filter = 'saturate(1.12) contrast(1.08)';
        ctx.drawImage(img, dx, dy, dw, dh);
        ctx.restore();

        // Feather edges (skip for cylinder — circle clip already softens edges)
        if (!isCylinder) {
          var feather = Math.round(Math.min(w, h) * 0.005);
          if (feather > 0) {
            ctx.save();
            ctx.globalCompositeOperation = 'destination-out';
            var gT = ctx.createLinearGradient(0, 0, 0, feather);
            gT.addColorStop(0, 'rgba(0,0,0,0.30)'); gT.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = gT; ctx.fillRect(0, 0, w, feather);
            var gB = ctx.createLinearGradient(0, h - feather, 0, h);
            gB.addColorStop(0, 'rgba(0,0,0,0)'); gB.addColorStop(1, 'rgba(0,0,0,0.30)');
            ctx.fillStyle = gB; ctx.fillRect(0, h - feather, w, feather);
            var gL = ctx.createLinearGradient(0, 0, feather, 0);
            gL.addColorStop(0, 'rgba(0,0,0,0.30)'); gL.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = gL; ctx.fillRect(0, 0, feather, h);
            var gR = ctx.createLinearGradient(w - feather, 0, w, 0);
            gR.addColorStop(0, 'rgba(0,0,0,0)'); gR.addColorStop(1, 'rgba(0,0,0,0.30)');
            ctx.fillStyle = gR; ctx.fillRect(w - feather, 0, feather, h);
            ctx.restore();
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = COLOR_ICING; ctx.fillRect(0, 0, w, h);
            ctx.restore();
          }
        }

        var canvasTex = new THREE.CanvasTexture(c);
        if (THREE.SRGBColorSpace) canvasTex.colorSpace = THREE.SRGBColorSpace;
        canvasTex.anisotropy = 16;
        canvasTex.generateMipmaps = false;
        canvasTex.minFilter = THREE.LinearFilter;
        canvasTex.magFilter = THREE.LinearFilter;
        topMaterial.map = canvasTex;
        topMaterial.needsUpdate = true;
      }, undefined, function (err) {
        console.warn('[ColdCake 3D] image load failed:', err);
      });
    }

    build();
  }

  function loadThree(callback) {
    if (typeof THREE !== 'undefined') { callback(); return; }
    var s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    s.onload = callback;
    s.onerror = function () { console.error('[ColdCake 3D] Three.js load failed'); };
    document.head.appendChild(s);
  }

  function start() {
    var widgets = document.querySelectorAll('.cc-cake-3d');
    if (!widgets.length) return;
    loadThree(function () { widgets.forEach(init); });
  }

  // Live API
  window.coldcakeCakeRebuild = function (widget) {
    if (!widget) return;
    if (widget._cc && widget._cc.destroy) widget._cc.destroy();
    if (typeof THREE === 'undefined') {
      loadThree(function () { init(widget); });
    } else {
      init(widget);
    }
  };
  window.coldcakeCakeApplyImage = function (widget, src) {
    if (!widget) return;
    if (widget._cc && widget._cc.applyImage) {
      widget._cc.applyImage(src);
    } else {
      widget.dataset.cakeImage = src || '';
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();